/* ==========================================================================
   B&B – Hero (bb-hero.js)
   Fade or slide transition, autoplay with pause, swipe, prev/next,
   theme-editor block selection, rounded-header state, scroll-over support.
   No dependency on Prestige's JS.
   ========================================================================== */

(() => {
  if (customElements.get('bb-hero')) return;

  class BBHero extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.bb-hero__slide'));
      this.track = this.querySelector('.bb-hero__track');
      this.total = this.slides.length;
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this.mode = this.dataset.transition === 'slide' ? 'slide' : 'fade';
      this.speed = Math.max(3000, parseInt(this.dataset.speed, 10) || 6000);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplay = this.dataset.autoplay === 'true' && this.total > 1 && !this.reducedMotion;
      this.pauses = { user: false, hover: false, focus: false, editor: false, covered: false };
      this.abort = new AbortController();

      const opts = { signal: this.abort.signal };

      this.setupSentinel();
      this.setupScrollCue(opts);
      this.setupVideos(opts);
      if (this.total < 2) return;

      this.counter = this.querySelector('[data-current]');
      this.pauseBtn = this.querySelector('[data-pause]');
      if (this.pauseBtn && !this.autoplay) this.pauseBtn.hidden = true;

      this.querySelector('[data-prev]')?.addEventListener('click', () => this.goTo(this.index - 1), opts);
      this.querySelector('[data-next]')?.addEventListener('click', () => this.goTo(this.index + 1), opts);
      this.pauseBtn?.addEventListener('click', () => this.togglePause(), opts);

      this.addEventListener('mouseenter', () => this.setPause('hover', true), opts);
      this.addEventListener('mouseleave', () => this.setPause('hover', false), opts);
      this.addEventListener('focusin', () => this.setPause('focus', true), opts);
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.setPause('focus', false);
      }, opts);
      document.addEventListener('visibilitychange', () => this.restart(), opts);

      this.setupSwipe(opts);
      this.setupEditor(opts);

      // Load the other slides' images after the page has loaded,
      // so they're ready before they appear (first slide stays top priority).
      const warm = () => {
        this.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
      };
      if (document.readyState === 'complete') setTimeout(warm, 300);
      else window.addEventListener('load', warm, { once: true, signal: this.abort.signal });

      this.restart();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.abort?.abort();
      this.sentinelObserver?.disconnect();
      this.sentinel?.remove();
      document.documentElement.classList.remove('bb-hero-passed');
    }

    /* ------------------------------------------------------------------
       A 1px marker placed right after the hero section, in normal page flow.
       The hero itself may be pinned (sticky), so we can't watch the hero —
       we watch where its bottom edge *would* be. When that marker passes
       the top of the screen, the header is stuck and the hero is covered.
       ------------------------------------------------------------------ */
    setupSentinel() {
      const wrapper = this.closest('.shopify-section') || this;
      this.sentinel = document.createElement('div');
      this.sentinel.className = 'bb-hero-sentinel';
      this.sentinel.setAttribute('aria-hidden', 'true');
      wrapper.after(this.sentinel);

      const radius = parseFloat(getComputedStyle(this).getPropertyValue('--bb-header-radius')) || 0;
      const edge = Math.round(radius) + 1;

      this.sentinelObserver = new IntersectionObserver(([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < edge;
        document.documentElement.classList.toggle('bb-hero-passed', passed);
        this.setPause('covered', passed);
      }, { rootMargin: `-${edge}px 0px 0px 0px` });

      this.sentinelObserver.observe(this.sentinel);
      this.setupClip();
    }

    /* ------------------------------------------------------------------
       When the page scrolls over a pinned hero, only draw the part of the
       hero that is above the announcement bar / header. Anything below
       is cut off, so the hero can never show through between the header
       and the first section, even if a phone browser briefly paints
       layers out of order while scrolling.
       ------------------------------------------------------------------ */
    setupClip() {
      const wrapper = this.closest('.shopify-section');
      if (!wrapper || getComputedStyle(wrapper).position !== 'sticky') return;

      const signal = this.abort.signal;
      let frame = null;
      const schedule = () => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = null;
          this.updateClip();
        });
      };

      window.addEventListener('scroll', schedule, { passive: true, signal });
      window.addEventListener('resize', schedule, { passive: true, signal });
      this.updateClip();
    }

    updateClip() {
      if (!this.sentinel) return;
      const heroBottom = this.getBoundingClientRect().bottom;
      const coverTop = this.sentinel.getBoundingClientRect().top;
      const cut = Math.max(0, heroBottom - coverTop);
      this.style.clipPath = cut > 0.5 ? `inset(0 0 ${Math.ceil(cut)}px 0)` : '';
    }

    get isPaused() {
      return document.hidden || Object.values(this.pauses).some(Boolean);
    }

    setPause(reason, value) {
      this.pauses[reason] = value;
      this.restart();
    }

    togglePause() {
      this.pauses.user = !this.pauses.user;
      this.pauseBtn.setAttribute('aria-pressed', String(this.pauses.user));
      this.pauseBtn.setAttribute('aria-label', this.pauses.user ? 'Play slideshow' : 'Pause slideshow');
      this.restart();
    }

    restart() {
      clearTimeout(this.timer);
      this.syncVideos();
      if (!this.autoplay || this.isPaused) return;

      // A slide set to "show the whole video" moves on when its video ends
      const video = this.activeVideo();
      if (video && this.slides[this.index].dataset.waitVideo === 'true' && !this.videoPaused) return;

      this.timer = setTimeout(() => this.goTo(this.index + 1), this.speed);
    }

    /* ------------------------------------------------------------------
       Video slides: only the video on the current slide plays, and only
       the version for this screen size (desktop or mobile). Videos pause
       while the hero is covered or the tab is hidden, and start paused
       for visitors who prefer reduced motion.
       ------------------------------------------------------------------ */
    setupVideos(opts) {
      this.videos = Array.from(this.querySelectorAll('video.bb-hero__video'));
      if (!this.videos.length) return;

      this.videoPaused = this.reducedMotion;
      this.videoToggle = this.querySelector('[data-video-toggle]');

      this.videos.forEach((video) => {
        video.muted = true;
        video.playsInline = true;
        video.removeAttribute('controls');
        video.addEventListener('ended', () => {
          if (video === this.activeVideo()) this.goTo(this.index + 1);
        }, opts);
      });

      this.videoToggle?.addEventListener('click', () => {
        this.videoPaused = !this.videoPaused;
        this.restart();
      }, opts);

      window.matchMedia('(max-width: 767px)').addEventListener('change', () => this.syncVideos(), opts);
      if (this.total < 2) {
        document.addEventListener('visibilitychange', () => this.syncVideos(), opts);
      }
      this.syncVideos();
    }

    activeVideo() {
      if (!this.videos || !this.videos.length) return null;
      const slide = this.slides[this.index];
      return Array.from(slide.querySelectorAll('video.bb-hero__video'))
        .find((video) => getComputedStyle(video).display !== 'none') || null;
    }

    syncVideos() {
      if (!this.videos || !this.videos.length) return;
      const active = this.activeVideo();
      const canPlay = !this.videoPaused && !document.hidden && !this.pauses.covered;

      this.videos.forEach((video) => {
        if (video === active && canPlay) {
          if (video.preload === 'none') video.preload = 'auto';
          const attempt = video.play();
          if (attempt && attempt.catch) attempt.catch(() => {});
        } else {
          video.pause();
          if (video !== active && video.currentTime > 0 && video.closest('.bb-hero__slide') !== this.slides[this.index]) {
            video.currentTime = 0; // start from the beginning next time
          }
        }
      });

      const slideHasVideo = Boolean(active);
      if (this.videoToggle) {
        this.videoToggle.hidden = !slideHasVideo;
        this.videoToggle.setAttribute('aria-pressed', String(this.videoPaused));
        this.videoToggle.setAttribute('aria-label', this.videoPaused ? 'Play video' : 'Pause video');
      }
    }

    goTo(target) {
      if (this.total < 2) return;
      const next = (target + this.total) % this.total;

      if (next !== this.index) {
        const prevSlide = this.slides[this.index];
        const nextSlide = this.slides[next];

        prevSlide.classList.remove('is-active');
        prevSlide.setAttribute('aria-hidden', 'true');
        prevSlide.inert = true;

        nextSlide.classList.add('is-active');
        nextSlide.removeAttribute('aria-hidden');
        nextSlide.inert = false;

        if (this.mode === 'slide') {
          this.track.style.transform = `translateX(${next * -100}%)`;
        }

        this.index = next;
        this.dataset.tone = nextSlide.dataset.tone || 'dark';
        if (this.counter) this.counter.textContent = String(next + 1).padStart(2, '0');
      }

      this.restart();
    }

    setupSwipe(opts) {
      let startX = null;
      let startY = null;

      this.track.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse') return; // touch and pen only; mouse uses the arrows
        startX = e.clientX;
        startY = e.clientY;
      }, opts);

      this.track.addEventListener('pointerup', (e) => {
        if (startX === null) return;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        startX = null;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.2) {
          this.goTo(this.index + (dx < 0 ? 1 : -1));
        }
      }, opts);

      this.track.addEventListener('pointercancel', () => { startX = null; }, opts);
    }

    setupEditor(opts) {
      if (!window.Shopify || !window.Shopify.designMode) return;

      // Clicking a slide block in Customize shows that slide and holds it there.
      document.addEventListener('shopify:block:select', (e) => {
        const i = this.slides.indexOf(e.target);
        if (i === -1) return;
        this.pauses.editor = true;
        this.goTo(i);
      }, opts);

      document.addEventListener('shopify:block:deselect', (e) => {
        if (this.slides.includes(e.target)) this.setPause('editor', false);
      }, opts);
    }

    setupScrollCue(opts) {
      const cue = this.querySelector('[data-scroll-cue]');
      if (!cue) return;

      // Scroll to where the hero ends: the sticky header lands at the top
      // with the first homepage section right under it. Uses the marker,
      // because a pinned hero's own position doesn't move.
      cue.addEventListener('click', (e) => {
        e.preventDefault();
        const top = this.sentinel.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top, behavior: this.reducedMotion ? 'auto' : 'smooth' });
      }, opts);
    }
  }

  customElements.define('bb-hero', BBHero);
})();